import React from "react";
import { useNavigate } from "react-router-dom";
import imgUrl from "../../imgUrl";
import useCategory from "../../apiHooks/useCategory";
import { useTranslation } from "react-i18next";

function Categories() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: categories = [], isLoading } = useCategory();

  // Split into parent categories (no parent_id) and build a map of children
  const parentCategories = categories.filter(
    (c) => !c.parent_id
  );
  const childMap = categories.reduce((acc, c) => {
    if (c.parent_id) {
      acc[c.parent_id] = acc[c.parent_id] || [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  // If the API returns flat list without parent_id, treat all as parents
  const isHierarchical = categories.some((c) => c.parent_id);
  const displayCategories = isHierarchical ? parentCategories : categories;

  const handleCategoryClick = (id) => {
    navigate(`/search?category_id=${id}`);
  };

  const skeletons = Array.from({ length: 12 });

  return (
    <div className="bg-gray-100 py-6 sm:py-10 lg:py-16">
      <div className="mx-auto max-w-screen-2xl px-3 sm:px-10">
        {/* Header */}
        <div className="mb-6 sm:mb-10 flex justify-center">
          <div className="text-center w-full lg:w-2/5">
            <h2 className="text-lg sm:text-xl lg:text-2xl mb-1 sm:mb-2 font-semibold">
              {t('categories.title')}
            </h2>
            <p className="text-sm sm:text-base font-sans text-gray-600 leading-5 sm:leading-6">
              {t('categories.desc')}
            </p>
          </div>
        </div>

        {/* Grid */}
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-2 sm:gap-3">
          {isLoading
            ? skeletons.map((_, i) => (
                <li key={i} className="animate-pulse">
                  <div className="flex h-full w-full border border-gray-100 shadow-sm bg-white p-4 rounded-lg">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="pl-4 flex-1 space-y-2 pt-1">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-2 bg-gray-100 rounded w-1/2" />
                      <div className="h-2 bg-gray-100 rounded w-2/3" />
                    </div>
                  </div>
                </li>
              ))
            : displayCategories.map((category) => {
                const children = childMap[category.id] || category.sub_categories || [];
                return (
                  <li
                    className="group"
                    key={category.id}
                  >
                    <div className="flex h-full w-full border border-gray-100 shadow-sm bg-white p-4 cursor-pointer transition duration-200 ease-linear transform group-hover:shadow-lg rounded-lg">
                      <div className="flex">
                        {/* Icon */}
                        <div className="flex items-start flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center overflow-hidden">
                            {category.image ? (
                              <img
                                alt={category.name}
                                src={`${imgUrl}/${category.image}`}
                                className="w-7 h-7 object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/* Name + subcategories */}
                        <div className="pl-3 flex-1 min-w-0">
                          {/* Parent category link */}
                          <h3 className="text-sm font-semibold leading-tight line-clamp-1">
                            <button
                              onClick={() => handleCategoryClick(category.id)}
                              className="text-gray-700 group-hover:text-emerald-500 cursor-pointer text-left hover:underline"
                            >
                              {category.name}
                            </button>
                          </h3>

                          {/* Sub-categories list */}
                          {children.length > 0 && (
                            <ul className="pt-1 mt-1 space-y-0.5">
                              {children.slice(0, 4).map((sub) => (
                                <li key={sub.id}>
                                  <button
                                    onClick={() => handleCategoryClick(sub.id)}
                                    className="flex items-center text-xs text-gray-400 hover:text-emerald-600 cursor-pointer w-full text-left transition-colors duration-150"
                                  >
                                    <svg
                                      className="w-3 h-3 mr-1 flex-shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                    <span className="truncate">{sub.name}</span>
                                  </button>
                                </li>
                              ))}
                              {children.length > 4 && (
                                <li>
                                  <button
                                    onClick={() => handleCategoryClick(category.id)}
                                    className="text-xs text-emerald-500 hover:text-emerald-700 cursor-pointer"
                                  >
                                  +{children.length - 4} {t('categories.more').replace('{{count}}', '')}
                                  </button>
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
        </ul>

      </div>
    </div>
  );
}

export default Categories;
